import { HttpEvent } from '@angular/common/http';
import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { UploadedFile } from './documents-table.model';

/**
 * Per-app wiring for DocumentsTableComponent - each app (company, executor,
 * client...) has its own file-upload endpoints/service, so this is provided
 * at the call site instead of the component depending on any one app's
 * HTTP service directly (same pattern as shared-chat's CHAT_CONFIG).
 */
export type DocumentsTableKind = 'documents' | 'photos';

/**
 * Optional server-side list params (UI audit 2026-10-28: this table's own
 * lazy-load event used to be ignored entirely, with totalDocuments faked
 * as documents.length). A config that doesn't accept this 4th arg still
 * works unchanged - it just won't get real pagination; the result array
 * may carry a `.total` property (see ListResult) when the backend reports
 * a real total, otherwise the component falls back to `.length`.
 */
export interface DocumentsListQuery {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
}

/** UploadedFile[] with an optional real server-reported total tagged on. */
export type DocumentsListResult = UploadedFile[] & { total?: number };

export interface DocumentsTableConfig {
  listDocuments: (userId: number, kind?: DocumentsTableKind, query?: DocumentsListQuery) => Observable<DocumentsListResult>;
  uploadDocument: (userId: number, file: File, kind?: DocumentsTableKind) => Observable<HttpEvent<{ success: boolean; data: UploadedFile }>>;
  deleteDocument: (fileId: number, kind?: DocumentsTableKind) => Observable<unknown>;
  getSignedUrl: (fileId: number) => Observable<string>;
  getFileContent: (fileId: number) => Observable<ArrayBuffer>;

  /**
   * Verification-review actions - optional, only present when the table is
   * used for a verification case's documents (mode="manager", or mode="user"
   * showing its own review status). All three act on the
   * VerificationDocument id (UploadedFile.verificationDocumentId), not the
   * file id. Omit entirely for plain file management (company/car/avatar
   * documents, which have no review workflow).
   */
  approveDocument?: (verificationDocumentId: number) => Observable<unknown>;
  rejectDocument?: (verificationDocumentId: number, reason?: string) => Observable<unknown>;
  setPendingDocument?: (verificationDocumentId: number) => Observable<unknown>;
}

export const DOCUMENTS_TABLE_CONFIG = new InjectionToken<DocumentsTableConfig>('DOCUMENTS_TABLE_CONFIG');
